import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { ChevronDown, ArrowRight, Menu, X, Search, Globe } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import SearchModal from './SearchModal'
import logo from '../assets/logo.png'
import { withSkipIntroParam } from '../utils/skipIntroUrl'
import { getCrossSiteUrl } from '../utils/crossSiteUrls'

export const calculatePanelPosition = (
  viewportWidth,
  viewportHeight,
  panelWidth,
  headerBottom,
  safeAreaLeft = 0,
  safeAreaRight = 0,
  minClearance = 16
) => {
  // Calculate panel width respecting max constraints
  const maxAllowedWidth = viewportWidth - 2 * minClearance - safeAreaLeft - safeAreaRight;
  const clampedPanelWidth = Math.min(panelWidth, maxAllowedWidth);
  
  // Calculate centered left position with clamping
  let left = (viewportWidth - clampedPanelWidth) / 2;
  const maxLeft = viewportWidth - clampedPanelWidth - minClearance - safeAreaRight;
  left = Math.max(minClearance + safeAreaLeft, Math.min(left, maxLeft));
  
  // Calculate top position (below header with small gap)
  const top = headerBottom + 4; // 4px gap
  // Ensure panel doesn't go below viewport (with some bottom margin)
  const maxTop = viewportHeight - 100; // arbitrary reserve for bottom
  const clampedTop = Math.min(top, maxTop);
  
  return {
    left: Math.round(left),
    top: Math.round(clampedTop),
    width: Math.round(clampedPanelWidth)
  };
};

const Header = ({ onLogoClick }) => {
  const { t } = useTranslation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [expandedMobileService, setExpandedMobileService] = useState(null)
  const navigate = useNavigate()
  const location = useLocation()
  const leaveTimerRef = useRef(null)
  const dropdownPanelRef = useRef(null)
  const headerRef = useRef(null)
  const triggerRef = useRef(null)
  const [panelStyle, setPanelStyle] = useState({ left: '0px', top: '0px', width: 'auto' })

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsSearchOpen(true)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    if (isMobileMenuOpen || isServiceDropdownOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen, isServiceDropdownOpen])

  useEffect(() => {
    setIsMobileMenuOpen(false)
    closeDropdown()
  }, [location.pathname])

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (leaveTimerRef.current) {
        clearTimeout(leaveTimerRef.current)
      }
    }
  }, [])



  const closeDropdown = () => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current)
      leaveTimerRef.current = null
    }
    setIsServiceDropdownOpen(false)
  }

  const keepDropdownOpen = () => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current)
      leaveTimerRef.current = null
    }
  }

  const startCloseTimer = () => {
    keepDropdownOpen() // Clear any existing timer
    leaveTimerRef.current = setTimeout(() => {
      closeDropdown()
    }, 500)
  }

  useEffect(() => {
    if (!isServiceDropdownOpen || !dropdownPanelRef.current || !headerRef.current) return;

    const updatePanelPosition = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const headerRect = headerRef.current.getBoundingClientRect();
      const safeAreaLeft = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-left') || '0', 10);
      const safeAreaRight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-right') || '0', 10);
      
      const panelWidth = dropdownPanelRef.current.offsetWidth;
      const { left, top, width } = calculatePanelPosition(
        viewportWidth,
        viewportHeight,
        panelWidth,
        headerRect.bottom,
        safeAreaLeft,
        safeAreaRight,
        16
      );
      
      setPanelStyle({
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`
      });
    };

    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition);
    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition);
    };
  }, [isServiceDropdownOpen]);

  // Focus management and keyboard navigation
  useEffect(() => {
    if (!isServiceDropdownOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeDropdown();
        triggerRef.current?.focus();
      }
    };

    const handleClickOutside = (e) => {
      if (dropdownPanelRef.current && !dropdownPanelRef.current.contains(e.target) && 
          triggerRef.current && !triggerRef.current.contains(e.target)) {
        closeDropdown();
      }
    };

    // Focus first focusable element in panel
    const focusFirstElement = () => {
      if (dropdownPanelRef.current) {
        const focusable = dropdownPanelRef.current.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable) {
          focusable.focus();
        }
      }
    };

    // Delay focus slightly to allow panel to render
    const focusTimer = setTimeout(focusFirstElement, 50);

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('click', handleClickOutside);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('click', handleClickOutside);
      // Return focus to trigger when dropdown closes
      if (triggerRef.current && document.activeElement === document.body) {
        triggerRef.current.focus();
      }
    };
  }, [isServiceDropdownOpen]);

  const megaMenuData = t('header.megaMenu', { returnObjects: true }) || {}
  const megaMenuArray = Array.isArray(megaMenuData) ? megaMenuData : Object.values(megaMenuData)
  const primarySections = megaMenuArray.slice(0, 2)
  const secondarySections = megaMenuArray.slice(2)

  return (
    <>
      <header 
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled
          ? 'bg-white/90 backdrop-blur-xl border-b border-border-light shadow-soft'
          : 'bg-white/60 backdrop-blur-md'
        }`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo Group */}
            <div className="flex items-center gap-4">
              {/* Logo — uses actual logo.png, click replays intro */}
              <button onClick={() => { if (onLogoClick) onLogoClick(); navigate('/'); }} className="flex items-center cursor-pointer shrink-0">
                <img src={logo} alt="AlgoMori" className="w-[140px] h-auto object-contain" />
              </button>
              
              {/* Tagline */}
              <div className="hidden lg:flex items-center gap-4">
                <div className="w-px h-6 bg-border-light"></div>
                <span className="text-xs font-semibold text-text-muted tracking-widest whitespace-nowrap">
                  Bioinformatics & AI for Life Sciences
                </span>
              </div>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-0.5 h-full">
              <div
                className="relative flex items-center"
                onMouseEnter={() => {
                  keepDropdownOpen()
                  setIsServiceDropdownOpen(true)
                }}
                onMouseLeave={startCloseTimer}
              >
                <button
                  ref={triggerRef}
                  onClick={() => { navigate('/service'); closeDropdown(); }}
                  className="flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium text-text-muted hover:text-primary transition-colors rounded-lg hover:bg-primary-50"
                >
                  {t('header.service')}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isServiceDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {isServiceDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 h-3 bg-transparent" />
                )}
                {isServiceDropdownOpen && (
                  <div 
                    ref={dropdownPanelRef}
                    style={panelStyle}
                    className="fixed pt-3 w-[94vw] max-w-7xl"
                    onMouseEnter={keepDropdownOpen}
                    onMouseLeave={startCloseTimer}
                  >
                    <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-8 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] border border-border-light/60 cursor-default transition-all max-h-[calc(100vh-100px)] overflow-y-auto overscroll-contain relative">
                      <button
                        onClick={closeDropdown}
                        className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-text-muted hover:text-text-main z-10"
                      >
                        <X className="w-5 h-5" />
                      </button>

                      {/* All Services button (moved to top) */}
                      <div className="mb-10 pb-6 border-b border-border-light/50 flex justify-start mt-2">
                        <button
                          onClick={() => { navigate('/service'); closeDropdown() }}
                          className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-50 text-sm font-bold text-primary hover:bg-primary hover:text-white transition-all group/all shadow-sm"
                        >
                          {t('header.allServices')}
                          <ArrowRight className="w-4 h-4 group-hover/all:translate-x-1 transition-transform" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 divide-y lg:divide-y-0 lg:divide-x divide-border-light/50">
                        {primarySections.map((section, idx) => (
                          <div key={idx} className="flex flex-col h-full lg:px-6 first:pl-0 last:pr-0">
                            <button 
                              onClick={() => { navigate(section.path || '/service'); closeDropdown() }}
                              className="text-left w-full group mb-6"
                            >
                              <h3 className="text-lg font-display font-bold text-text-main group-hover:text-primary transition-colors flex items-center gap-2">
                                {typeof section === 'object' ? section.title : ''}
                                <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                              </h3>
                            </button>
                            {typeof section === 'object' && section.categories && section.categories.length > 0 && (
                              <div className="flex-1">
                                <div className="border-l-2 border-primary/30 pl-5 ml-1 py-1">
                                  <div className="space-y-5">
                                  {section.categories.map((cat, cIdx) => (
                                    <div key={cIdx}>
                                      {cat.path ? (
                                        <button 
                                          onClick={() => { navigate(cat.path); closeDropdown() }}
                                          className="text-left w-full focus:outline-none group/cat"
                                        >
                                          <h4 className="text-[13px] font-bold text-text-main mb-1.5 uppercase tracking-wider group-hover/cat:text-primary transition-colors leading-relaxed">{cat.title}</h4>
                                        </button>
                                      ) : (
                                        <h4 className="text-[13px] font-bold text-text-main mb-1.5 uppercase tracking-wider leading-relaxed">{cat.title}</h4>
                                      )}
                                      
                                      {cat.items && cat.items.length > 0 && (
                                        <ul className="space-y-1.5">
                                          {cat.items.map((item, iIdx) => {
                                            const label = typeof item === 'object' ? item.label : item;
                                            const itemPath = typeof item === 'object' ? item.path : null;
                                            return (
                                              <li key={iIdx} className="text-[13px] text-text-muted hover:text-primary hover:translate-x-1 transition-all cursor-pointer">
                                                {itemPath ? (
                                                  <button 
                                                    onClick={(e) => { e.stopPropagation(); navigate(itemPath); closeDropdown(); }}
                                                    className="text-left w-full focus:outline-none"
                                                  >
                                                    {label}
                                                  </button>
                                                ) : (
                                                  <span>{label}</span>
                                                )}
                                              </li>
                                            );
                                          })}
                                        </ul>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                            )}
                          </div>
                        ))}
                        {/* Third column for secondary sections */}
                        <div className="flex flex-col h-full lg:px-6 last:pr-0">
                          <div className="space-y-6">
                            {secondarySections.map((section, idx) => (
                              <div key={idx} className="flex flex-col">
                                {/* Consultancy section: wrapped in a subtle card for visual grouping */}
                                {section.key === 'consultancy' ? (
                                  <div className="flex flex-col">
                                    <button 
                                      onClick={() => { navigate(section.path || '/service'); closeDropdown() }}
                                      className="text-left w-full group mb-5"
                                    >
                                      <h3 className="text-lg font-display font-bold text-text-main group-hover:text-primary transition-colors flex items-center gap-2">
                                        {typeof section === 'object' ? section.title : ''}
                                        <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                                      </h3>
                                    </button>
                                    {section.categories && section.categories.length > 0 && (
                                      <div className="border-l-2 border-primary/30 pl-5 ml-1 py-1">
                                        <div className="space-y-5">
                                          {section.categories.map((cat, cIdx) => (
                                            <div key={cIdx}>
                                              {cat.path ? (
                                                <button 
                                                  onClick={() => { navigate(cat.path); closeDropdown() }}
                                                  className="text-left w-full focus:outline-none group/cat"
                                                >
                                                  <h4 className="text-[13px] font-bold text-text-main mb-1.5 uppercase tracking-wider group-hover/cat:text-primary transition-colors leading-relaxed">{cat.title}</h4>
                                                </button>
                                              ) : (
                                                <h4 className="text-[13px] font-bold text-text-main mb-1.5 uppercase tracking-wider leading-relaxed">{cat.title}</h4>
                                              )}
                                              
                                              {cat.items && cat.items.length > 0 && (
                                                <ul className="space-y-1.5">
                                                  {cat.items.map((item, iIdx) => {
                                                    const label = typeof item === 'object' ? item.label : item;
                                                    const itemPath = typeof item === 'object' ? item.path : null;
                                                    return (
                                                      <li key={iIdx} className="text-[13px] text-text-muted hover:text-primary hover:translate-x-1 transition-all cursor-pointer">
                                                        {itemPath ? (
                                                          <button 
                                                            onClick={(e) => { e.stopPropagation(); navigate(itemPath); closeDropdown(); }}
                                                            className="text-left w-full focus:outline-none"
                                                          >
                                                            {label}
                                                          </button>
                                                        ) : (
                                                          <span>{label}</span>
                                                        )}
                                                      </li>
                                                    );
                                                  })}
                                                </ul>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  /* Writing & Data Security sections: clean standalone items */
                                  <div className={`flex flex-col ${section.key === 'writing' ? 'pt-2' : ''}`}>
                                    <button 
                                      onClick={() => { navigate(section.path || '/service'); closeDropdown() }}
                                      className="text-left w-full group mb-3"
                                    >
                                      <h3 className="text-lg font-display font-bold text-text-main group-hover:text-primary transition-colors flex items-center gap-2">
                                        {typeof section === 'object' ? section.title : ''}
                                        <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                                      </h3>
                                    </button>
                                    {typeof section === 'object' && section.categories && section.categories.length > 0 && (
                                      <div className="space-y-6">
                                        {section.categories.map((cat, cIdx) => (
                                          <div key={cIdx}>
                                            {cat.path ? (
                                              <button 
                                                onClick={() => { navigate(cat.path); closeDropdown() }}
                                                className="text-left w-full focus:outline-none group/cat"
                                              >
                                                <h4 className="text-sm font-bold text-text-main mb-2 uppercase tracking-wider group-hover/cat:text-primary transition-colors">{cat.title}</h4>
                                              </button>
                                            ) : (
                                              <h4 className="text-sm font-bold text-text-main mb-2 uppercase tracking-wider">{cat.title}</h4>
                                            )}
                                            
                                            {cat.items && cat.items.length > 0 && (
                                              <ul className="space-y-1.5">
                                                {cat.items.map((item, iIdx) => {
                                                  const label = typeof item === 'object' ? item.label : item;
                                                  const itemPath = typeof item === 'object' ? item.path : null;
                                                  return (
                                                    <li key={iIdx} className="text-[13px] text-text-muted hover:text-primary hover:translate-x-1 transition-all cursor-pointer">
                                                      {itemPath ? (
                                                        <button 
                                                          onClick={(e) => { e.stopPropagation(); navigate(itemPath); closeDropdown(); }}
                                                          className="text-left w-full focus:outline-none"
                                                        >
                                                          {label}
                                                        </button>
                                                      ) : (
                                                        <span>{label}</span>
                                                      )}
                                                    </li>
                                                  );
                                                })}
                                              </ul>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <Link to="/why-us" className="px-3 py-2.5 text-sm font-medium text-text-muted hover:text-primary transition-colors rounded-lg hover:bg-primary-50">
                {t('header.whyUs')}
              </Link>
              <Link to="/about" className="px-3 py-2.5 text-sm font-medium text-text-muted hover:text-primary transition-colors rounded-lg hover:bg-primary-50">
                {t('header.aboutUs')}
              </Link>
              <Link to="/news" className="px-3 py-2.5 text-sm font-medium text-text-muted hover:text-primary transition-colors rounded-lg hover:bg-primary-50">
                {t('header.news')}
              </Link>
              <Link to="/technical-resources" className="px-3 py-2.5 text-sm font-medium text-text-muted hover:text-primary transition-colors rounded-lg hover:bg-primary-50">
                {t('header.techResources')}
              </Link>
            </nav>

            {/* Actions */}
            <div className="hidden md:flex items-center gap-3">
              <button 
                onClick={() => setIsSearchOpen(true)}
                className="p-2.5 text-text-muted hover:text-primary transition-colors rounded-lg hover:bg-primary-50 flex items-center gap-2"
                title={t('header.search')}
              >
                <Search className="w-5 h-5" />
              </button>

              <a
                href={withSkipIntroParam(getCrossSiteUrl('jp'))}
                className="p-2.5 text-text-muted hover:text-primary transition-colors rounded-lg hover:bg-primary-50 flex items-center gap-2 text-sm font-medium"
                title="Switch Language"
              >
                <Globe className="w-5 h-5" />
                <span>日本語</span>
              </a>

              <Link to="/contact" className="btn-primary px-5 py-2.5 text-sm flex items-center gap-2">
                {t('header.contactUs')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Mobile actions */}
            <div className="flex md:hidden items-center gap-2">
              <button 
                onClick={() => setIsSearchOpen(true)}
                className="p-2 text-text-muted hover:text-primary transition-colors"
              >
                <Search className="w-6 h-6" />
              </button>
              <a
                href={withSkipIntroParam(getCrossSiteUrl('jp'))}
                className="p-2.5 text-text-muted hover:text-primary transition-colors rounded-lg hover:bg-primary-50 flex items-center gap-1.5 text-sm font-medium"
                title="Switch Language"
              >
                <Globe className="w-5 h-5" />
                <span>日本語</span>
              </a>
              <button 
                onClick={() => {
                  setIsMobileMenuOpen(!isMobileMenuOpen);
                  if (!isMobileMenuOpen) setExpandedMobileService(null);
                }} 
                className="p-2 text-text-muted hover:text-primary transition-colors"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-border-light max-h-[calc(100vh-80px)] overflow-y-auto overscroll-contain">
            <div className="max-w-7xl mx-auto px-6 py-6 space-y-2">
              <p className="text-xs font-semibold text-text-dim uppercase tracking-widest mb-3 px-3">{t('header.service')}</p>
              <div className="space-y-4">
                {megaMenuArray.map((section, idx) => (
                  <div key={idx} className="bg-surface-50/50 rounded-2xl p-4 border border-border-light/40">
                    <button 
                      onClick={() => { 
                        if (typeof section === 'object' && section.categories && section.categories.length > 0) {
                          setExpandedMobileService(expandedMobileService === idx ? null : idx);
                        } else {
                          navigate(section.path || '/service'); 
                          setIsMobileMenuOpen(false);
                        }
                      }} 
                      className="flex items-center justify-between w-full text-left font-bold text-text-main group"
                    >
                      {typeof section === 'object' ? section.title : ''}
                      {typeof section === 'object' && section.categories && section.categories.length > 0 ? (
                        <ChevronDown className={`w-4 h-4 text-primary opacity-70 transition-transform ${expandedMobileService === idx ? 'rotate-180' : ''}`} />
                      ) : (
                        <ArrowRight className="w-4 h-4 text-primary opacity-70 group-hover:translate-x-1 transition-transform" />
                      )}
                    </button>
                    {expandedMobileService === idx && typeof section === 'object' && section.categories && section.categories.length > 0 && (
                      <div className="pl-3 space-y-5 pt-4 mt-3 border-l-2 border-primary/20 border-t border-border-light">
                        <div className="mb-1">
                          <button 
                             onClick={() => { navigate(section.path || '/service'); setIsMobileMenuOpen(false) }} 
                             className="text-xs font-bold text-primary flex items-center gap-1 hover:text-primary-dark transition-colors"
                          >
                             Explore All <ArrowRight className="w-3 h-3" />
                          </button>
                        </div>
                        {section.categories.map((cat, cIdx) => (
                          <div key={cIdx} className="pl-2">
                            {cat.path ? (
                              <button 
                                onClick={() => { navigate(cat.path); setIsMobileMenuOpen(false) }}
                                className="text-left w-full focus:outline-none group/mcat"
                              >
                                <div className="text-xs font-bold text-text-main uppercase tracking-wider mb-2 group-hover/mcat:text-primary transition-colors">{cat.title}</div>
                              </button>
                            ) : (
                              <div className="text-xs font-bold text-text-main uppercase tracking-wider mb-2">{cat.title}</div>
                            )}
                            
                            {cat.items && cat.items.length > 0 && (
                              <ul className="space-y-2">
                                {cat.items.map((item, iIdx) => {
                                  const label = typeof item === 'object' ? item.label : item;
                                  const itemPath = typeof item === 'object' ? item.path : null;
                                  return (
                                    <li key={iIdx} className="text-[13px] text-text-muted">
                                      {itemPath ? (
                                        <button 
                                          onClick={(e) => { e.stopPropagation(); navigate(itemPath); setIsMobileMenuOpen(false); }}
                                          className="text-left w-full hover:text-primary transition-colors focus:outline-none"
                                        >
                                          {label}
                                        </button>
                                      ) : (
                                        <span>{label}</span>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="divider-green my-4" />
              <Link to="/why-us" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-3 text-sm font-medium text-text-muted hover:text-primary transition-colors rounded-xl hover:bg-primary-50">{t('header.whyUs')}</Link>
              <Link to="/about" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-3 text-sm font-medium text-text-muted hover:text-primary transition-colors rounded-xl hover:bg-primary-50">{t('header.aboutUs')}</Link>
              <Link to="/service-process" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-3 text-sm font-medium text-text-muted hover:text-primary transition-colors rounded-xl hover:bg-primary-50">{t('header.serviceProcess')}</Link>
              <Link to="/service/data-security" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-3 text-sm font-medium text-text-muted hover:text-primary transition-colors rounded-xl hover:bg-primary-50">{t('header.dataSecurity')}</Link>
              <Link to="/news" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-3 text-sm font-medium text-text-muted hover:text-primary transition-colors rounded-xl hover:bg-primary-50">{t('header.news')}</Link>
              <Link to="/technical-resources" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-3 text-sm font-medium text-text-muted hover:text-primary transition-colors rounded-xl hover:bg-primary-50">{t('header.techResources')}</Link>
              <div className="pt-4">
                <Link to="/contact" className="btn-primary px-5 py-3 text-sm flex items-center justify-center gap-2 w-full">
                  {t('header.contactUs')} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  )
}

export default Header
